import React, { useEffect, useState } from 'react';
import { PackageIcon, PlusIcon, SearchIcon, EditIcon, TrashIcon, ShoppingCartIcon, CalendarIcon, UserIcon, TagIcon, DollarSignIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
import { FormSection } from '../ui/FormSection';
import { Pagination } from '../ui/Pagination';
import { SortableHeader } from '../ui/SortableHeader';
import { Product, ProductSale, ProductSaleItem } from '../../types';
import { formatCurrency, getToday } from '../../utils/helpers';
import { usePagination } from '../../hooks/usePagination';
import { useSortableData } from '../../hooks/useSortableData';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { supabase } from '../../utils/supabase';
// Helper to normalize names for comparison
function normalizeName(name: string) {
  if (!name) return '';
  return name.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/^(dr\.|dra\.|lic\.|ing\.|ter\.)\s*/g, '').replace(/\s+/g, ' ');
}
const CATEGORIES = [{
  value: 'Cuidado Facial',
  label: 'Cuidado Facial'
}, {
  value: 'Cuidado Corporal',
  label: 'Cuidado Corporal'
}, {
  value: 'Suplementos',
  label: 'Suplementos'
}, {
  value: 'Equipos',
  label: 'Equipos'
}, {
  value: 'Accesorios',
  label: 'Accesorios'
}, {
  value: 'Otro',
  label: 'Otro'
}];
// ─── helpers: Supabase row → app type ────────────────────────────────────────
function rowToProduct(row: any): Product {
  return {
    id: row.id,
    name: row.name,
    sku: row.sku,
    description: row.description ?? '',
    price: Number(row.price),
    cost: Number(row.cost),
    stock: row.stock,
    category: row.category,
    status: row.status,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at)
  };
}
function rowToSale(row: any): ProductSale {
  return {
    id: row.id,
    date: row.date,
    patientId: row.patient_id ?? '',
    items: row.items as ProductSaleItem[],
    total: Number(row.total),
    notes: row.notes ?? '',
    createdByUserId: row.created_by_user_id,
    createdByUserName: row.created_by_user_name,
    createdAt: new Date(row.created_at)
  };
}
export function Products() {
  const {
    patients,
    addAuditLog
  } = useData();
  const {
    user
  } = useAuth();
  const {
    addNotification
  } = useNotifications();
  const [activeTab, setActiveTab] = useState('catalog');
  const [loading, setLoading] = useState(true);
  // Data State
  const [products, setProducts] = useState<Product[]>([]);
  const [sales, setSales] = useState<ProductSale[]>([]);
  const [sellers, setSellers] = useState<{
    id: string;
    name: string;
  }[]>([]);
  // ─── Load Data from Supabase ─────────────────────────────────────────────
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [{
          data: productsData,
          error: productsError
        }, {
          data: salesData,
          error: salesError
        }, {
          data: usersData
        }, {
          data: doctorsData
        }] = await Promise.all([supabase.from('products').select('*').order('created_at', {
          ascending: false
        }), supabase.from('product_sales').select('*').order('date', {
          ascending: false
        }), supabase.from('users').select('id, name, role'), supabase.from('doctors').select('id, name')]);
        if (productsError) throw productsError;
        if (salesError) throw salesError;
        setProducts((productsData ?? []).map(rowToProduct));
        setSales((salesData ?? []).map(rowToSale));
        // Build sellers list: users with allowed roles + doctors, deduplicated by name
        const allowedRoles = new Set(['doctor', 'doctora', 'terapeuta', 'terapista', 'recepcion', 'recepción', 'recepcionista', 'callcenter', 'call center', 'call_center']);
        const sellersList: {
          id: string;
          name: string;
        }[] = [];
        const seenNormalizedNames = new Set<string>();
        (usersData ?? []).forEach((u: any) => {
          if (!allowedRoles.has((u.role || '').toLowerCase())) return;
          const norm = normalizeName(u.name);
          if (norm && !seenNormalizedNames.has(norm)) {
            seenNormalizedNames.add(norm);
            sellersList.push({
              id: u.id,
              name: u.name
            });
          }
        });
        (doctorsData ?? []).forEach((d: any) => {
          const norm = normalizeName(d.name);
          if (norm && !seenNormalizedNames.has(norm)) {
            seenNormalizedNames.add(norm);
            sellersList.push({
              id: d.id,
              name: d.name
            });
          }
        });
        setSellers(sellersList);
      } catch (e) {
        console.error('Error loading data from Supabase:', e);
        addNotification({
          type: 'error',
          title: 'Error de carga',
          message: 'No se pudieron cargar los productos desde la base de datos.'
        });
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);
  // ─── CATALOG TAB STATE ───────────────────────────────────────────────────
  const [productSearch, setProductSearch] = useState('');
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState({
    name: '',
    sku: '',
    description: '',
    price: '',
    cost: '',
    stock: '',
    category: 'Cuidado Facial',
    status: 'active' as 'active' | 'inactive'
  });
  const [productErrors, setProductErrors] = useState<Record<string, string>>({});
  const filteredProducts = products.filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()) || p.sku.toLowerCase().includes(productSearch.toLowerCase()) || p.category.toLowerCase().includes(productSearch.toLowerCase()));
  const {
    sortedItems: sortedProducts,
    requestSort: requestProductSort,
    isSorted: isProductSorted
  } = useSortableData(filteredProducts);
  const {
    paginatedItems: paginatedProducts,
    currentPage: productPage,
    totalPages: productTotalPages,
    totalItems: productTotalItems,
    startIndex: productStartIndex,
    endIndex: productEndIndex,
    goToPage: productGoToPage,
    nextPage: productNextPage,
    prevPage: productPrevPage
  } = usePagination(sortedProducts);
  const handleOpenProductModal = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name,
        sku: product.sku,
        description: product.description,
        price: product.price.toString(),
        cost: product.cost.toString(),
        stock: product.stock.toString(),
        category: product.category,
        status: product.status
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: '',
        sku: '',
        description: '',
        price: '',
        cost: '',
        stock: '',
        category: 'Cuidado Facial',
        status: 'active'
      });
    }
    setProductErrors({});
    setIsProductModalOpen(true);
  };
  const handleSaveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!productForm.name.trim()) errors.name = 'El nombre es requerido';
    if (!productForm.sku.trim()) errors.sku = 'El SKU es requerido';
    if (!productForm.price || isNaN(Number(productForm.price))) errors.price = 'Precio inválido';
    if (!productForm.cost || isNaN(Number(productForm.cost))) errors.cost = 'Costo inválido';
    if (!productForm.stock || isNaN(Number(productForm.stock))) errors.stock = 'Stock inválido';
    if (Object.keys(errors).length > 0) {
      setProductErrors(errors);
      return;
    }
    const payload = {
      name: productForm.name,
      sku: productForm.sku,
      description: productForm.description,
      price: Number(productForm.price),
      cost: Number(productForm.cost),
      stock: Number(productForm.stock),
      category: productForm.category,
      status: productForm.status
    };
    try {
      if (editingProduct) {
        const {
          data,
          error
        } = await supabase.from('products').update(payload).eq('id', editingProduct.id).select().single();
        if (error) throw error;
        setProducts(products.map(p => p.id === editingProduct.id ? rowToProduct(data) : p));
        addNotification({
          type: 'success',
          title: 'Producto Actualizado',
          message: `${productForm.name} ha sido actualizado.`
        });
        addAuditLog({
          userId: user?.id || 'system',
          userName: user?.name || 'Sistema',
          action: 'update',
          module: 'products',
          entityId: editingProduct.id,
          entityName: productForm.name,
          details: `Producto actualizado: ${productForm.name} (SKU: ${productForm.sku}) - Precio: ${formatCurrency(Number(productForm.price))}, Stock: ${productForm.stock}`
        });
      } else {
        const {
          data,
          error
        } = await supabase.from('products').insert(payload).select().single();
        if (error) throw error;
        setProducts([rowToProduct(data), ...products]);
        addNotification({
          type: 'success',
          title: 'Producto Creado',
          message: `${productForm.name} ha sido agregado al catálogo.`
        });
        addAuditLog({
          userId: user?.id || 'system',
          userName: user?.name || 'Sistema',
          action: 'create',
          module: 'products',
          entityId: data.id,
          entityName: productForm.name,
          details: `Producto creado: ${productForm.name} (SKU: ${productForm.sku}) - Precio: ${formatCurrency(Number(productForm.price))}, Stock: ${productForm.stock}`
        });
      }
      setIsProductModalOpen(false);
    } catch (e: any) {
      console.error('Error saving product:', e);
      addNotification({
        type: 'error',
        title: 'Error',
        message: e.message ?? 'No se pudo guardar el producto.'
      });
    }
  };
  const handleDeleteProduct = async (id: string) => {
    if (!confirm('¿Está seguro de que desea eliminar este producto?')) return;
    try {
      const deletedProduct = products.find(p => p.id === id);
      const {
        error
      } = await supabase.from('products').delete().eq('id', id);
      if (error) throw error;
      setProducts(products.filter(p => p.id !== id));
      addNotification({
        type: 'success',
        title: 'Producto Eliminado',
        message: 'El producto ha sido eliminado del catálogo.'
      });
      addAuditLog({
        userId: user?.id || 'system',
        userName: user?.name || 'Sistema',
        action: 'delete',
        module: 'products',
        entityId: id,
        entityName: deletedProduct?.name || 'Producto',
        details: `Producto eliminado: ${deletedProduct?.name || 'Producto'} (SKU: ${deletedProduct?.sku || 'N/A'})`
      });
    } catch (e: any) {
      console.error('Error deleting product:', e);
      addNotification({
        type: 'error',
        title: 'Error',
        message: e.message ?? 'No se pudo eliminar el producto.'
      });
    }
  };
  // ─── SALES TAB STATE ─────────────────────────────────────────────────────
  const [saleSearch, setSaleSearch] = useState('');
  const [saleDateFrom, setSaleDateFrom] = useState('');
  const [saleDateTo, setSaleDateTo] = useState('');
  const [isSaleModalOpen, setIsSaleModalOpen] = useState(false);
  const [saleForm, setSaleForm] = useState({
    patientId: '',
    sellerId: '',
    date: getToday(),
    notes: ''
  });
  const [saleItems, setSaleItems] = useState<{
    productId: string;
    quantity: number;
    discount: number;
  }[]>([]);
  const [saleErrors, setSaleErrors] = useState<Record<string, string>>({});
  const filteredSales = sales.filter(s => {
    const patient = patients.find(p => p.id === s.patientId);
    const matchesSearch = !saleSearch || patient?.name.toLowerCase().includes(saleSearch.toLowerCase()) || s.createdByUserName.toLowerCase().includes(saleSearch.toLowerCase());
    const matchesDateFrom = !saleDateFrom || s.date >= saleDateFrom;
    const matchesDateTo = !saleDateTo || s.date <= saleDateTo;
    return matchesSearch && matchesDateFrom && matchesDateTo;
  });
  const {
    sortedItems: sortedSales,
    requestSort: requestSaleSort,
    isSorted: isSaleSorted
  } = useSortableData(filteredSales);
  const {
    paginatedItems: paginatedSales,
    currentPage: salePage,
    totalPages: saleTotalPages,
    totalItems: saleTotalItems,
    startIndex: saleStartIndex,
    endIndex: saleEndIndex,
    goToPage: saleGoToPage,
    nextPage: saleNextPage,
    prevPage: salePrevPage
  } = usePagination(sortedSales);
  const handleOpenSaleModal = () => {
    setSaleForm({
      patientId: '',
      sellerId: '',
      date: getToday(),
      notes: ''
    });
    setSaleItems([{
      productId: '',
      quantity: 1,
      discount: 0
    }]);
    setSaleErrors({});
    setIsSaleModalOpen(true);
  };
  const addSaleItem = () => setSaleItems([...saleItems, {
    productId: '',
    quantity: 1,
    discount: 0
  }]);
  const removeSaleItem = (index: number) => setSaleItems(saleItems.filter((_, i) => i !== index));
  const updateSaleItem = (index: number, field: 'productId' | 'quantity' | 'discount', value: any) => {
    const newItems = [...saleItems];
    newItems[index] = {
      ...newItems[index],
      [field]: value
    };
    setSaleItems(newItems);
    // Clear any row-specific or global "items" error so stale messages don't linger
    setSaleErrors(prev => {
      if (!prev[`item_${index}`] && !prev.items) return prev;
      const next = {
        ...prev
      };
      delete next[`item_${index}`];
      delete next.items;
      return next;
    });
  };
  const calculateSaleTotal = () => saleItems.reduce((total, item) => {
    if (!item.productId) return total;
    const product = products.find(p => p.id === item.productId);
    if (!product) return total;
    const lineTotal = product.price * (item.quantity || 0);
    const discountAmount = lineTotal * ((item.discount || 0) / 100);
    return total + lineTotal - discountAmount;
  }, 0);
  const handleSaveSale = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!saleForm.patientId) errors.patientId = 'Debe seleccionar un cliente/paciente';
    if (!saleForm.sellerId) errors.sellerId = 'Debe seleccionar un vendedor';
    if (!saleForm.date) errors.date = 'La fecha es requerida';
    const validItems = saleItems.filter(item => item.productId && item.quantity > 0);
    if (validItems.length === 0) errors.items = 'Debe agregar al menos un producto válido';
    validItems.forEach((item, idx) => {
      const product = products.find(p => p.id === item.productId);
      if (product && product.stock < item.quantity) errors[`item_${idx}`] = `Stock insuficiente (${product.stock} disponibles)`;
    });
    if (Object.keys(errors).length > 0) {
      setSaleErrors(errors);
      return;
    }
    const finalItems: ProductSaleItem[] = validItems.map(item => {
      const product = products.find(p => p.id === item.productId)!;
      const lineTotal = product.price * item.quantity;
      const discountAmount = lineTotal * ((item.discount || 0) / 100);
      return {
        productId: item.productId,
        productName: product.name,
        quantity: item.quantity,
        unitPrice: product.price,
        discount: item.discount || 0,
        subtotal: lineTotal - discountAmount
      };
    });
    const total = finalItems.reduce((sum, item) => sum + item.subtotal, 0);
    // Use selected seller instead of logged-in user
    const selectedSeller = sellers.find(s => s.id === saleForm.sellerId);
    const sellerUserId = selectedSeller?.id ?? user?.id ?? 'unknown';
    const sellerUserName = selectedSeller?.name ?? user?.name ?? 'Usuario';
    try {
      // Insert sale
      const {
        data: saleData,
        error: saleError
      } = await supabase.from('product_sales').insert({
        date: saleForm.date,
        patient_id: saleForm.patientId || null,
        items: finalItems,
        total,
        notes: saleForm.notes,
        created_by_user_id: sellerUserId,
        created_by_user_name: sellerUserName
      }).select().single();
      if (saleError) throw saleError;
      // Update stock for each product sold
      await Promise.all(finalItems.map(item => {
        const product = products.find(p => p.id === item.productId)!;
        return supabase.from('products').update({
          stock: product.stock - item.quantity
        }).eq('id', item.productId);
      }));
      // Update local state
      setSales([rowToSale(saleData), ...sales]);
      setProducts(products.map(p => {
        const soldItem = finalItems.find(item => item.productId === p.id);
        return soldItem ? {
          ...p,
          stock: p.stock - soldItem.quantity
        } : p;
      }));
      addNotification({
        type: 'success',
        title: 'Venta Registrada',
        message: `Venta por ${formatCurrency(total)} registrada exitosamente.`
      });
      const patientName = patients.find(p => p.id === saleForm.patientId)?.name || 'Cliente';
      const itemsSummary = finalItems.map(item => {
        const base = `${item.quantity}x ${item.productName}`;
        return item.discount ? `${base} (-${item.discount}% desc.)` : base;
      }).join(', ');
      const hasAnyDiscount = finalItems.some(item => (item.discount || 0) > 0);
      const discountNote = hasAnyDiscount ? ` | Descuento aplicado: ${finalItems.filter(item => (item.discount || 0) > 0).map(item => `${item.productName}: ${item.discount}% (${formatCurrency(item.unitPrice * item.quantity - item.subtotal)})`).join(', ')}` : '';
      addAuditLog({
        userId: user?.id || 'system',
        userName: user?.name || 'Sistema',
        action: 'create',
        module: 'product_sales',
        entityId: saleData.id,
        entityName: `Venta a ${patientName}`,
        details: `Venta registrada por ${sellerUserName}: ${itemsSummary} - Total: ${formatCurrency(total)} - Cliente: ${patientName}${discountNote}`
      });
      setIsSaleModalOpen(false);
    } catch (e: any) {
      console.error('Error saving sale:', e);
      addNotification({
        type: 'error',
        title: 'Error',
        message: e.message ?? 'No se pudo registrar la venta.'
      });
    }
  };
  const tabs = [{
    id: 'catalog',
    label: 'Catálogo de Productos',
    icon: <PackageIcon className="w-4 h-4" data-id="element-3643" />
  }, {
    id: 'sales',
    label: 'Ventas de Productos',
    icon: <ShoppingCartIcon className="w-4 h-4" data-id="element-3644" />
  }];
  return <div className="space-y-6" data-id="element-3645">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-600 via-orange-500 to-rose-500 p-6 text-white shadow-xl" data-id="element-3646">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-3647" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" data-id="element-3648">
          <div data-id="element-3649">
            <div className="flex items-center gap-3 mb-2" data-id="element-3650">
              <div className="p-2.5 bg-white/15 backdrop-blur-sm rounded-xl" data-id="element-3651">
                <PackageIcon className="w-6 h-6" data-id="element-3652" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-3653">Productos</h1>
            </div>
            <p className="text-orange-100 max-w-xl" data-id="element-3654">
              Gestión de inventario y ventas de productos
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-3655">
            {activeTab === 'catalog' ? <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3657" />} onClick={() => handleOpenProductModal()} className="bg-white text-orange-700 hover:bg-orange-50 shadow-lg shadow-orange-900/20" data-id="element-3656">
                Nuevo Producto
              </Button> : <Button leftIcon={<ShoppingCartIcon className="w-4 h-4" data-id="element-3659" />} onClick={handleOpenSaleModal} className="bg-white text-orange-700 hover:bg-orange-50 shadow-lg shadow-orange-900/20" data-id="element-3658">
                Registrar Venta
              </Button>}
          </div>
        </div>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-3660" />

      {/* CATALOG TAB */}
      {activeTab === 'catalog' && <Card padding="none" data-id="element-3661">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700" data-id="element-3662">
            <div className="relative max-w-md" data-id="element-3663">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-3664" />
              <Input placeholder="Buscar por nombre, SKU o categoría..." value={productSearch} onChange={e => setProductSearch(e.target.value)} className="pl-10" data-id="element-3665" />
            </div>
          </div>

          {/* ── MOBILE CARDS ── */}
          <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3666">
            {paginatedProducts.length === 0 ? <div className="py-8 text-center text-gray-400 text-sm" data-id="element-3667">
                No se encontraron productos
              </div> : paginatedProducts.map(product => <div key={product.id} className="px-4 py-3 flex items-center gap-3" data-id="element-3668">
                  <div className="w-9 h-9 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400 flex-shrink-0" data-id="element-3669">
                    <PackageIcon className="w-4 h-4" data-id="element-3670" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-3671">
                    <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-3672">
                      {product.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-3673">
                      {product.category} · SKU: {product.sku}
                    </p>
                    <div className="flex items-center gap-2 mt-1" data-id="element-3674">
                      <span className="text-xs font-semibold text-gray-900 dark:text-white" data-id="element-3675">
                        {formatCurrency(product.price)}
                      </span>
                      <span className={`text-xs font-medium ${product.stock <= 5 ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}`} data-id="element-3676">
                        Stock: {product.stock}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0" data-id="element-3677">
                    <button onClick={() => handleOpenProductModal(product)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" data-id="element-3678">
                      <EditIcon className="w-4 h-4" data-id="element-3679" />
                    </button>
                    <button onClick={() => handleDeleteProduct(product.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-3680">
                      <TrashIcon className="w-4 h-4" data-id="element-3681" />
                    </button>
                  </div>
                </div>)}
          </div>

          {/* ── DESKTOP TABLE ── */}
          <div className="hidden sm:block overflow-x-auto" data-id="element-3682">
            <table className="w-full" data-id="element-3683">
              <thead data-id="element-3684">
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50" data-id="element-3685">
                  <SortableHeader label="Producto" sortKey="name" currentDirection={isProductSorted('name')} onSort={requestProductSort} data-id="element-3686" />

                  <SortableHeader label="SKU" sortKey="sku" currentDirection={isProductSorted('sku')} onSort={requestProductSort} className="hidden sm:table-cell" data-id="element-3687" />

                  <SortableHeader label="Categoría" sortKey="category" currentDirection={isProductSorted('category')} onSort={requestProductSort} className="hidden sm:table-cell" data-id="element-3688" />

                  <SortableHeader label="Precio" sortKey="price" currentDirection={isProductSorted('price')} onSort={requestProductSort} data-id="element-3689" />

                  <SortableHeader label="Stock" sortKey="stock" currentDirection={isProductSorted('stock')} onSort={requestProductSort} data-id="element-3690" />

                  <SortableHeader label="Estado" sortKey="status" currentDirection={isProductSorted('status')} onSort={requestProductSort} data-id="element-3691" />

                  <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3692">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3693">
                {loading ? <tr data-id="element-3694">
                    <td colSpan={7} className="py-8 text-center text-gray-400" data-id="element-3695">
                      Cargando productos...
                    </td>
                  </tr> : paginatedProducts.length === 0 ? <tr data-id="element-3696">
                    <td colSpan={7} className="py-8 text-center text-gray-500" data-id="element-3697">
                      No se encontraron productos
                    </td>
                  </tr> : paginatedProducts.map(product => <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-3698">
                      <td className="py-3 px-4" data-id="element-3699">
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-3700">
                          {product.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate max-w-[200px]" data-id="element-3701">
                          {product.description}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300 hidden sm:table-cell" data-id="element-3702">
                        {product.sku}
                      </td>
                      <td className="py-3 px-4 hidden sm:table-cell" data-id="element-3703">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300" data-id="element-3704">
                          <TagIcon className="w-3 h-3" data-id="element-3705" />
                          {product.category}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-medium text-gray-900 dark:text-white" data-id="element-3706">
                        {formatCurrency(product.price)}
                      </td>
                      <td className="py-3 px-4" data-id="element-3707">
                        <span className={`font-medium ${product.stock <= 5 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`} data-id="element-3708">
                          {product.stock}
                        </span>
                      </td>
                      <td className="py-3 px-4" data-id="element-3709">
                        <Badge variant={product.status === 'active' ? 'success' : 'default'} data-id="element-3710">
                          {product.status === 'active' ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-3711">
                        <div className="flex justify-end gap-2" data-id="element-3712">
                          <button onClick={() => handleOpenProductModal(product)} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-3713">
                            <EditIcon className="w-4 h-4" data-id="element-3714" />
                          </button>
                          <button onClick={() => handleDeleteProduct(product.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-3715">
                            <TrashIcon className="w-4 h-4" data-id="element-3716" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-gray-200 dark:border-gray-700" data-id="element-3717">
            <Pagination currentPage={productPage} totalPages={productTotalPages} totalItems={productTotalItems} startIndex={productStartIndex} endIndex={productEndIndex} onPageChange={productGoToPage} onNext={productNextPage} onPrev={productPrevPage} data-id="element-3718" />
          </div>
        </Card>}

      {/* SALES TAB */}
      {activeTab === 'sales' && <Card padding="none" data-id="element-3719">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col md:flex-row gap-4" data-id="element-3720">
            <div className="relative flex-1" data-id="element-3721">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-3722" />
              <Input placeholder="Buscar por cliente o vendedor..." value={saleSearch} onChange={e => setSaleSearch(e.target.value)} className="pl-10" data-id="element-3723" />
            </div>
            <div className="flex items-center gap-2" data-id="element-3724">
              <Input type="date" value={saleDateFrom} onChange={e => setSaleDateFrom(e.target.value)} data-id="element-3725" />

              <span className="text-gray-400" data-id="element-3726">→</span>
              <Input type="date" value={saleDateTo} onChange={e => setSaleDateTo(e.target.value)} data-id="element-3727" />
            </div>
          </div>

          {/* ── MOBILE CARDS VENTAS ── */}
          <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3728">
            {paginatedSales.length === 0 ? <div className="py-8 text-center text-gray-400 text-sm" data-id="element-3729">
                No se encontraron ventas
              </div> : paginatedSales.map(sale => {
          const patient = patients.find(p => p.id === sale.patientId);
          const saleItems = sale.items || [];
          return <div key={sale.id} className="px-4 py-3 flex items-start gap-3" data-id="element-3730">
                    <div className="flex-1 min-w-0" data-id="element-3731">
                      <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-3732">
                        {patient?.name || sale.patientName || 'Cliente'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-3733">
                        {sale.date} · {sale.createdByUserName || ''}
                      </p>
                      <p className="text-xs text-gray-400 truncate" data-id="element-3734">
                        {saleItems.map((i: any) => {
                  const name = i.productName || '';
                  const disc = i.discount ? ` (-${i.discount}%)` : '';
                  return `${i.quantity || 1}x ${name}${disc}`;
                }).join(', ')}
                      </p>
                      <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400 mt-1" data-id="element-3735">
                        {formatCurrency(sale.total)}
                      </p>
                    </div>
                  </div>;
        })}
          </div>

          {/* ── DESKTOP TABLE VENTAS ── */}
          <div className="hidden sm:block overflow-x-auto" data-id="element-3736">
            <table className="w-full" data-id="element-3737">
              <thead data-id="element-3738">
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50" data-id="element-3739">
                  <SortableHeader label="Fecha" sortKey="date" currentDirection={isSaleSorted('date')} onSort={requestSaleSort} data-id="element-3740" />

                  <SortableHeader label="Cliente/Paciente" sortKey="patientId" currentDirection={isSaleSorted('patientId')} onSort={requestSaleSort} data-id="element-3741" />

                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3742">
                    Productos
                  </th>
                  <SortableHeader label="Total" sortKey="total" currentDirection={isSaleSorted('total')} onSort={requestSaleSort} data-id="element-3743" />

                  <SortableHeader label="Vendedor" sortKey="createdByUserName" currentDirection={isSaleSorted('createdByUserName')} onSort={requestSaleSort} data-id="element-3744" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3745">
                {loading ? <tr data-id="element-3746">
                    <td colSpan={5} className="py-8 text-center text-gray-400" data-id="element-3747">
                      Cargando ventas...
                    </td>
                  </tr> : paginatedSales.length === 0 ? <tr data-id="element-3748">
                    <td colSpan={5} className="py-8 text-center text-gray-500" data-id="element-3749">
                      No se encontraron ventas
                    </td>
                  </tr> : paginatedSales.map(sale => {
              const patient = patients.find(p => p.id === sale.patientId);
              return <tr key={sale.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-3750">
                        <td className="py-3 px-4 text-sm text-gray-900 dark:text-white" data-id="element-3751">
                          {sale.date}
                        </td>
                        <td className="py-3 px-4 font-medium text-gray-900 dark:text-white" data-id="element-3752">
                          {patient?.name || 'Desconocido'}
                        </td>
                        <td className="py-3 px-4" data-id="element-3753">
                          <div className="flex flex-col gap-1" data-id="element-3754">
                            {sale.items.map((item, idx) => {
                      const product = products.find(p => p.id === item.productId);
                      const hasDiscount = (item as any).discount > 0;
                      return <span key={idx} className="text-xs text-gray-600 dark:text-gray-300" data-id="element-3755">
                                  {item.quantity}x{' '}
                                  {product?.name || item.productName || 'Producto eliminado'}
                                  {hasDiscount && <span className="ml-1 text-emerald-600 dark:text-emerald-400 font-medium" data-id="element-3756">
                                      (-{(item as any).discount}%)
                                    </span>}
                                </span>;
                    })}
                          </div>
                        </td>
                        <td className="py-3 px-4 font-bold text-orange-600 dark:text-orange-400" data-id="element-3757">
                          {formatCurrency(sale.total)}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500 flex items-center gap-1.5 mt-1" data-id="element-3758">
                          <UserIcon className="w-3.5 h-3.5" data-id="element-3759" />
                          {sale.createdByUserName}
                        </td>
                      </tr>;
            })}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-gray-200 dark:border-gray-700" data-id="element-3760">
            <Pagination currentPage={salePage} totalPages={saleTotalPages} totalItems={saleTotalItems} startIndex={saleStartIndex} endIndex={saleEndIndex} onPageChange={saleGoToPage} onNext={saleNextPage} onPrev={salePrevPage} data-id="element-3761" />
          </div>
        </Card>}

      {/* Product Modal */}
      <Modal isOpen={isProductModalOpen} onClose={() => setIsProductModalOpen(false)} title={editingProduct ? 'Editar Producto' : 'Nuevo Producto'} size="md" icon={<PackageIcon className="w-6 h-6" data-id="element-3763" />} accentColor="amber" data-id="element-3762">
        <form onSubmit={handleSaveProduct} className="space-y-6" data-id="element-3764">
          <FormSection title="Información Básica" data-id="element-3765">
            <div className="space-y-4" data-id="element-3766">
              <Input label="Nombre del Producto" value={productForm.name} onChange={e => setProductForm({
              ...productForm,
              name: e.target.value
            })} error={productErrors.name} data-id="element-3767" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3768">
                <Input label="SKU (Código)" value={productForm.sku} onChange={e => setProductForm({
                ...productForm,
                sku: e.target.value
              })} error={productErrors.sku} data-id="element-3769" />

                <Select label="Categoría" options={CATEGORIES} value={productForm.category} onChange={v => setProductForm({
                ...productForm,
                category: v
              })} data-id="element-3770" />
              </div>
              <Textarea label="Descripción" value={productForm.description} onChange={e => setProductForm({
              ...productForm,
              description: e.target.value
            })} rows={2} data-id="element-3771" />
            </div>
          </FormSection>

          <FormSection title="Inventario y Precios" data-id="element-3772">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-3773">
              <Input type="number" label="Costo" value={productForm.cost} onChange={e => setProductForm({
              ...productForm,
              cost: e.target.value
            })} error={productErrors.cost} data-id="element-3774" />

              <Input type="number" label="Precio Venta" value={productForm.price} onChange={e => setProductForm({
              ...productForm,
              price: e.target.value
            })} error={productErrors.price} data-id="element-3775" />

              <Input type="number" label="Stock" value={productForm.stock} onChange={e => setProductForm({
              ...productForm,
              stock: e.target.value
            })} error={productErrors.stock} data-id="element-3776" />
            </div>
            <div className="mt-4" data-id="element-3777">
              <Select label="Estado" options={[{
              value: 'active',
              label: 'Activo'
            }, {
              value: 'inactive',
              label: 'Inactivo'
            }]} value={productForm.status} onChange={v => setProductForm({
              ...productForm,
              status: v as any
            })} data-id="element-3778" />
            </div>
          </FormSection>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800" data-id="element-3779">
            <Button variant="outline" type="button" onClick={() => setIsProductModalOpen(false)} data-id="element-3780">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-3781">
              {editingProduct ? 'Guardar Cambios' : 'Crear Producto'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Sale Modal */}
      <Modal isOpen={isSaleModalOpen} onClose={() => setIsSaleModalOpen(false)} title="Registrar Venta" size="lg" icon={<ShoppingCartIcon className="w-6 h-6" data-id="element-3783" />} accentColor="amber" data-id="element-3782">
        <form onSubmit={handleSaveSale} className="space-y-6" data-id="element-3784">
          <FormSection title="Detalles de la Venta" data-id="element-3785">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3786">
              <Select label="Cliente / Paciente" options={patients.map(p => ({
              value: p.id,
              label: p.name
            }))} value={saleForm.patientId} onChange={v => setSaleForm({
              ...saleForm,
              patientId: v
            })} error={saleErrors.patientId} searchable data-id="element-3787" />

              <Select label="Vendedor" options={sellers.map(s => ({
              value: s.id,
              label: s.name
            }))} value={saleForm.sellerId} onChange={v => setSaleForm({
              ...saleForm,
              sellerId: v
            })} error={saleErrors.sellerId} searchable placeholder="Seleccionar vendedor..." data-id="element-3788" />

              <Input type="date" label="Fecha" value={saleForm.date} onChange={e => setSaleForm({
              ...saleForm,
              date: e.target.value
            })} error={saleErrors.date} data-id="element-3789" />
            </div>
          </FormSection>

          <FormSection title="Productos" data-id="element-3790">
            {saleErrors.items && <p className="text-sm text-red-500 mb-2" data-id="element-3791">{saleErrors.items}</p>}
            <div className="space-y-3" data-id="element-3792">
              {saleItems.map((item, idx) => <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-3793">
                  <div className="flex-1" data-id="element-3794">
                    <Select options={products.filter(p => p.status === 'active').map(p => ({
                  value: p.id,
                  label: `${p.name} (${p.stock} disp.) - ${formatCurrency(p.price)}`
                }))} value={item.productId} onChange={v => updateSaleItem(idx, 'productId', v)} searchable placeholder="Seleccionar producto..." data-id="element-3795" />

                    {saleErrors[`item_${idx}`] && <p className="text-xs text-red-500 mt-1" data-id="element-3796">
                        {saleErrors[`item_${idx}`]}
                      </p>}
                  </div>
                  <div className="w-20" data-id="element-3797">
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block" data-id="element-3798">
                      Cant.
                    </label>
                    <Input type="number" min="1" value={item.quantity.toString()} onChange={e => updateSaleItem(idx, 'quantity', parseInt(e.target.value) || 1)} data-id="element-3799" />
                  </div>
                  <div className="w-20" data-id="element-3800">
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block" data-id="element-3801">
                      Desc. %
                    </label>
                    <Input type="number" min="0" max="100" value={item.discount.toString()} onChange={e => updateSaleItem(idx, 'discount', Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))} data-id="element-3802" />
                  </div>
                  {item.productId && item.discount > 0 && <div className="flex flex-col justify-end pb-1" data-id="element-3803">
                      <span className="text-xs text-gray-400 line-through" data-id="element-3804">
                        {formatCurrency((products.find(p => p.id === item.productId)?.price || 0) * item.quantity)}
                      </span>
                      <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400" data-id="element-3805">
                        {formatCurrency((products.find(p => p.id === item.productId)?.price || 0) * item.quantity * (1 - item.discount / 100))}
                      </span>
                    </div>}
                  <button type="button" onClick={() => removeSaleItem(idx)} className="p-2 mt-1 text-red-500 hover:bg-red-50 rounded-lg" disabled={saleItems.length === 1} data-id="element-3806">
                    <TrashIcon className="w-5 h-5" data-id="element-3807" />
                  </button>
                </div>)}
              <Button type="button" variant="outline" size="sm" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3809" />} onClick={addSaleItem} data-id="element-3808">
                Agregar otro producto
              </Button>
            </div>
            <div className="mt-6 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl flex items-center justify-between border border-orange-100 dark:border-orange-800" data-id="element-3810">
              <span className="text-orange-800 dark:text-orange-300 font-medium" data-id="element-3811">
                Total de la Venta:
              </span>
              <span className="text-2xl font-bold text-orange-600 dark:text-orange-400" data-id="element-3812">
                {formatCurrency(calculateSaleTotal())}
              </span>
            </div>
          </FormSection>

          <FormSection title="Notas Adicionales" data-id="element-3813">
            <Textarea value={saleForm.notes} onChange={e => setSaleForm({
            ...saleForm,
            notes: e.target.value
          })} rows={2} placeholder="Observaciones sobre la venta..." data-id="element-3814" />
          </FormSection>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800" data-id="element-3815">
            <Button variant="outline" type="button" onClick={() => setIsSaleModalOpen(false)} data-id="element-3816">
              Cancelar
            </Button>
            <Button type="submit" leftIcon={<DollarSignIcon className="w-4 h-4" data-id="element-3818" />} data-id="element-3817">
              Registrar Venta
            </Button>
          </div>
        </form>
      </Modal>
    </div>;
}